import { ErrorSummary, FieldMessage } from "../../shared/forms/ErrorSummary";
import { useFormErrors } from "../../shared/forms/useFormErrors";
import { useMutation } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { isApiProblem, problemMessage } from "../../shared/api/problem";
import type { Locale } from "../../shared/api/types";
import { authText } from "../../shared/i18n/authText";
import { useLocale } from "../../shared/lib/locale-context";
import { login } from "./api";

/**
 * Auth rulings (16 Sep, SA Rev 29): 423 `account-locked` after 5 failed attempts (locked 15 minutes); 429 `rate-limited`
 * (wait a minute), distinct from locked; 401 `unauthenticated` for an unknown user or a wrong password gets one generic
 * message that never says which.
 */
function loginError(error: unknown, locale: Locale): string {
  if (isApiProblem(error)) {
    if (error.status === 423 || error.code === "account-locked") return authText("loginLocked", locale);
    if (error.status === 429 || error.code === "rate-limited") return authText("loginRateLimited", locale);
    if (error.status === 401) return authText("loginIncorrect", locale);
  }
  return problemMessage(error);
}

/**
 * Paste and password managers are allowed (WCAG 3.3.8). Batch 18 (GRF-46): on the login page the e-mail field also offers a
 * passkey in its autofill (`passkeyAutofill`), and a password submit stops that waiting request (`onPasswordStart`) and lets it
 * wait again when the password is refused (`onPasswordFailed`).
 */
export function LoginForm({
  onSignedIn,
  submitLabel,
  passkeyAutofill = false,
  onPasswordStart,
  onPasswordFailed,
}: {
  onSignedIn: () => void | Promise<void>;
  submitLabel?: string;
  passkeyAutofill?: boolean;
  onPasswordStart?: () => void;
  onPasswordFailed?: () => void;
}) {
  const id = useId();
  const locale = useLocale();
  // Uncontrolled inputs (password managers fill them without React); typing is mirrored here so a missing field's
  // error goes the moment it is filled (shared form-error pattern). A wrong email/password names no field on purpose.
  const [values, setValues] = useState({ email: "", password: "" });
  const form = useFormErrors({
    values,
    validate: (current) => [
      ...(current.email.trim() ? [] : [{ field: "email", message: authText("loginEmailMissing", locale) }]),
      ...(current.password ? [] : [{ field: "password", message: authText("loginPasswordMissing", locale) }]),
    ],
    order: ["email", "password"],
    idOf: (field) => `${id}-${field}`,
  });
  const mutation = useMutation({
    mutationFn: (input: { email: string; password: string }) => login(input.email, input.password),
    meta: { authFlow: true },
    onSuccess: async () => {
      form.clearServer();
      await onSignedIn();
    },
    onError: (e) => {
      form.setServer({ fieldIssues: [], general: [] }, loginError(e, locale));
      onPasswordFailed?.();
    },
  });

  function readValues(element: HTMLFormElement) {
    const data = new FormData(element);
    const text = (name: string) => {
      const value = data.get(name);
      return typeof value === "string" ? value : "";
    };
    return { email: text("email"), password: text("password") };
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const current = readValues(event.currentTarget);
    setValues(current);
    if (!form.attempt(current)) return;
    onPasswordStart?.();
    mutation.mutate({ email: current.email.trim(), password: current.password });
  }

  return (
    <form className="gf-form stack" onSubmit={onSubmit} onChange={(event) => setValues(readValues(event.currentTarget))} noValidate>
      <ErrorSummary {...form.summary} />
      <div className="gf-field">
        <label htmlFor={`${id}-email`}>{authText("loginEmail", locale)}</label>
        <input {...form.controlProps("email")} name="email" type="email" autoComplete={passkeyAutofill ? "username webauthn" : "username"} required className="in" />
        <FieldMessage id={`${id}-email`} message={form.errorOf("email")} />
      </div>
      <div className="gf-field">
        <label htmlFor={`${id}-password`}>{authText("loginPassword", locale)}</label>
        <input {...form.controlProps("password")} name="password" type="password" autoComplete="current-password" required className="in" />
        <FieldMessage id={`${id}-password`} message={form.errorOf("password")} />
      </div>
      <button className="btn" type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? authText("loginBusy", locale) : (submitLabel ?? authText("loginSubmit", locale))}
      </button>
    </form>
  );
}
